"use client";

import { readActiveChatIds } from "./core/activeChatStorage";
import { workspaceChatTransport } from "./core/orgChatTransport";
import { createUnifiedChat, type UnifiedChat } from "./core/unifiedChat";
import { applyDefaultStreamSideEffects } from "@/components/analysis/chat/v2/utils/chatStreamUtils";
import { useSessionWorkspace } from "@/components/providers/org_provider";
import { useUser } from "@/components/providers/user_provider";
import { useIsFeatureFlagEnabledForUserViaContext } from "@/components/ui/FeatureFlagGatedComp";
import { workspaceChatBackend } from "@/components/unified-chat/core/orgChatBackend";
import { fireAndForget } from "@/lib/vercelUtils";
import { UnifiedChat as UnifiedChatFlag } from "@/utils/feature_flag/featureFlags";
import type { OrganizationId } from "@/utils/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type UnifiedChatContextValue = {
  unifiedChat: UnifiedChat;
  orgId: OrganizationId;
};

// Null while the flag is off or no org is selected.
export const UnifiedChatContext = createContext<UnifiedChatContextValue | null>(
  null
);

export const UnifiedChatProvider = ({ children }: { children: ReactNode }) => {
  const { org: workspace } = useSessionWorkspace();
  const { user } = useUser();
  const isUnifiedChatEnabled = useIsFeatureFlagEnabledForUserViaContext(
    UnifiedChatFlag.key,
    { onlyActiveOrg: true }
  );
  const queryClient = useQueryClient();
  // The layout survives sign-out, so each user gets a fresh store and never sees the previous user's chats.
  const [session, setSession] = useState(() => ({
    userId: user?.id,
    unifiedChat: createUnifiedChat({
      transport: workspaceChatTransport,
      initialActiveChatIds: readActiveChatIds(),
    }),
  }));
  if (session.userId !== user?.id) {
    setSession({
      userId: user?.id,
      unifiedChat: createUnifiedChat({
        transport: workspaceChatTransport,
        initialActiveChatIds: {},
      }),
    });
  }
  const { unifiedChat } = session;
  useEffect(() => () => unifiedChat.dispose(), [unifiedChat]);

  const workspaceId = isUnifiedChatEnabled ? workspace?.id : undefined;

  useEffect(
    () =>
      unifiedChat.subscribeToEvents(({ event }) => {
        applyDefaultStreamSideEffects({ event, queryClient });
        if (event.type === "init" || event.type === "finish") {
          fireAndForget(
            queryClient.invalidateQueries({
              queryKey: [workspaceChatBackend.listQueryKey],
            })
          );
        }
      }),
    [unifiedChat, queryClient]
  );

  const value = useMemo(
    () => (workspaceId ? { unifiedChat, orgId: workspaceId } : null),
    [unifiedChat, workspaceId]
  );

  return (
    <UnifiedChatContext.Provider value={value}>
      {children}
    </UnifiedChatContext.Provider>
  );
};
